import { NavLink } from 'react-router-dom'
import {
  Home, Star, Link2, Trophy, Wallet, History, LineChart, CreditCard, HelpCircle,
  FlaskConical, Lock, MessageSquare, Ticket,
} from 'lucide-react'
import { useHome } from '@/hooks/useHome'
import { cn } from '@/lib/utils'

/** P0.56 (Sección 4 del encargo): sidebar = navegación COMPLETA de escritorio, agrupada por
 * intención real (PRINCIPAL/PERSONAL/BLACK/MI CUENTA/AYUDA) -- nunca una lista plana ni un
 * "/mas" oculto detrás de una hamburguesa (`AppHeader` esconde ese botón en desktop, ver
 * `md:hidden` ahí). Todo lo que un usuario de escritorio necesita vive aquí; solo móvil/
 * Telegram sigue usando "Más" para lo secundario (BottomNav + MorePage, Sección 5). */
const PRINCIPAL_ITEMS = [
  { to: '/', label: 'Inicio', icon: Home, end: true },
  { to: '/picks', label: 'Picks', icon: Star },
  { to: '/parlays', label: 'Parlays', icon: Link2 },
  { to: '/reto', label: 'Reto', icon: Trophy },
  { to: '/quiniela', label: 'La Quiniela', icon: Ticket },
]

const PERSONAL_ITEMS = [
  { to: '/banca', label: 'Banca', icon: Wallet },
  { to: '/mas/evolucion', label: 'Mi Evolución', icon: LineChart },
]

const ACCOUNT_ITEMS = [
  { to: '/mas/historial', label: 'Historial', icon: History },
  { to: '/mas/favoritos', label: 'Favoritos', icon: Star },
  { to: '/mas/suscripcion', label: 'Suscripción', icon: CreditCard },
]

const HELP_ITEMS = [
  { to: '/mas/ayuda', label: 'Cómo funciona', icon: HelpCircle },
]

function NavRow({ to, label, icon: Icon, end }: { to: string; label: string; icon: typeof Home; end?: boolean }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-semibold transition-colors',
          isActive ? 'bg-brand-soft/40 text-brand' : 'text-text-dim hover:text-text hover:bg-panel-elevated/60'
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span>{label}</span>
    </NavLink>
  )
}

function NavGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <p className="px-3 pb-1.5 eyebrow text-text-dim/60">{label}</p>
      <nav className="space-y-0.5">{children}</nav>
    </div>
  )
}

/** JA Lab (Sección 4/16 del encargo): visible SIEMPRE, para los 3 planes -- FREE/PRO lo ven
 * bloqueado (candado + badge BLACK), BLACK entra a foundation. Gating por `plan` en el
 * frontend (no existe todavía un flag de backend propio de JA Lab -- P0.57 lo construirá;
 * se reutiliza `personal_intelligence`, ya resuelto exclusivamente para BLACK/ADMIN en
 * `plan_tiers.py`, como la misma señal real que ya usa el resto de contenido BLACK). */
function JALabRow({ unlocked }: { unlocked: boolean }) {
  return (
    <NavLink
      to="/ja-lab"
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-semibold transition-colors border',
          isActive
            ? 'bg-exact-soft/30 text-exact border-exact/40'
            : 'text-text-dim border-exact/20 hover:text-exact hover:bg-exact-soft/10'
        )
      }
    >
      <FlaskConical className="h-4 w-4 shrink-0" />
      <span className="flex-1">John Anthony Lab</span>
      {!unlocked && <Lock className="h-3 w-3 shrink-0 text-text-dim/70" />}
    </NavLink>
  )
}

export function DesktopNav() {
  const { data } = useHome(true)
  const isBlack = Boolean(data?.plan_features?.personal_intelligence)

  return (
    <aside className="hidden md:flex md:flex-col md:w-60 md:shrink-0 md:sticky md:top-16 md:h-[calc(100vh-4rem)] md:overflow-y-auto md:border-r md:border-border md:px-3 md:py-6 md:gap-7">
      <NavGroup label="Principal">
        {PRINCIPAL_ITEMS.map((item) => <NavRow key={item.to} {...item} />)}
      </NavGroup>

      <NavGroup label="Personal">
        {PERSONAL_ITEMS.map((item) => <NavRow key={item.to} {...item} />)}
      </NavGroup>

      <div className="space-y-0.5">
        <p className="px-3 pb-1.5 eyebrow text-exact/60">Black</p>
        <JALabRow unlocked={isBlack} />
      </div>

      <NavGroup label="Mi cuenta">
        {ACCOUNT_ITEMS.map((item) => <NavRow key={item.to} {...item} />)}
      </NavGroup>

      <NavGroup label="Ayuda">
        {HELP_ITEMS.map((item) => <NavRow key={item.to} {...item} />)}
        <a
          href="https://t.me/Jhon_sportbot"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-semibold text-text-dim hover:text-text hover:bg-panel-elevated/60 transition-colors"
        >
          <MessageSquare className="h-4 w-4 shrink-0" />
          <span>Soporte</span>
        </a>
      </NavGroup>
    </aside>
  )
}
